/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import find from 'lodash.find';
import isObject from 'lodash.isobject';
import isUndefined from 'lodash.isundefined';
import {TreeNode} from 'rc-tree';
import {keyIncrement} from './utils/TreeViewUtils';
import AssetLoader from './AssetLoader';

interface LoaderProps {
  id: number,
  groupId: number,
  remaining: number,
  key: string,
  groupName: string,
  label: string,
  isLoader: boolean,
  isLoading: boolean,
  loadingPending: boolean,
  isAsset: boolean
  isDevice?: boolean
};

interface SingleNodeProps {
  key: string,
  id: string| number,
  title: string,
  isAsset: boolean,
  isLeaf: boolean,
  isDevice: boolean,
  className: string
};

/**
  * The createTree method manages the creation of individual Tree
  * nodes within the Tree. A Tree node can either be of type group
  * (Account), or Asset, whereby group nodes represent specific
  * groups within the Account hierarchy, and asset nodes represent
  * specfic assets defined within a given group.
  *
  * @method createTree
  * @param {React.Component} container The container component for which
  *        the Tree is to be created.
  * @param {Immutable.List|Array} data The items to be added to the tree.
  *        Either the initial entire tree structure, or the children of a
  *        given non-leaf node.
  * @param {boolean} force Used to indicate that the tree, or a given tree
  *        node hierarchy is to be created even if container.omitCreateTree
  *        is true. Defaults to false.
  * @param {boolean} omitAssets Used to indicate that the tree is only to
  *        render groups; hence, assets are to be omitted.
  */
// eslint-disable-next-line max-params
export const createTree = ({
  container,
  data,
  force,
  loadingPending,
  omitAssets = false
}) => {
  if (!container?.omitCreateTree || force) {
    const items = data?.toJS ? [data?.toJS()] : data;
    container.omitCreateTree = true;
    return items.map(item => {
      const {id, name, title, key, isAsset, groupId, groupName, isLoading, assetsLoaded, isLoader, remaining} = item;
      const enhance  = !assetsLoaded;
      const children = (item.children = enhance ? (item.children || []) : item.children);
      const label = name || title;
      if (enhance && !isLoader) {
        children.push({
          groupId   : id,
          groupName : label,
          isAsset   : true,
          isLoader  : true,
          remaining,
          isLoading,
          key       : `${key}-${keyIncrement}`
        });
      }
      if (children && !isAsset) {
        const options = {
          container : {
            omitCreateTree : false
          },
          data  : children,
          force : true,
          loadingPending,
          omitAssets
        };
        const items = createTree(options);
        let isLeaf = !items.length;
        if (omitAssets && !isLeaf) {
          isLeaf = isUndefined(find(items, isObject));
          if (isLeaf) {
            return (
              <TreeNode id={id} key={key} title={label} isLeaf={isLeaf} isAsset={false} />
            );
          }
        }
        items.push(<TreeNode className={'asset-hidden'} key={`-${key}`} title={id} id={id} isAsset isLeaf />);
        return (
          <TreeNode id={id} key={key} title={label} isLeaf={isLeaf} isAsset={false}>{items}</TreeNode>
        );
      }
      if (!isAsset) {
        return (
          <TreeNode id={id} key={key} title={label} isAsset={false} isLeaf />
        );
      }
      if (!omitAssets) {
        return createLoaderNode({
          id,
          groupId,
          remaining,
          key,
          groupName,
          label,
          isLoader,
          isLoading,
          loadingPending,
          isAsset
        });
      }
    });
  }
};

/**
 * The createSingleNode method manages the creation of single Tree nodes
 *
 * ```typescript
 *
 * import {createSingleNode} from '@ion/dls/src/selectiontree/utils';
 *
 * const assetNode = createSingleNode(params);
 *
 * ```
 */
export const createSingleNode = ({
  key,
  id,
  title,
  isAsset,
  isDevice,
  isLeaf,
  className
}: SingleNodeProps) => (
  <TreeNode className={className}
    id={id}
    key={key}
    title={title}
    isDevice={isDevice}
    isAsset={isAsset}
    isLeaf={isLeaf} />
);

/**
 * The createLoaderNode method manages the creation of loader (...) Tree nodes
 *
 * ```typescript
 *
 * import {createLoaderNode} from '@ion/dls/src/selectiontree/utils';
 *
 * const loaderNode = createLoaderNode(params);
 *
 * ```
 */
export const createLoaderNode = ({
  id,
  groupId,
  remaining,
  key,
  groupName,
  label,
  isLoader,
  isLoading,
  loadingPending,
  isAsset,
  isDevice = false
}: LoaderProps) => (
  <TreeNode id={id}
    className={isLoader ? 'loader' : 'asset'}
    key={key}
    title={isLoader ? (<AssetLoader isLoading={isLoading}
      treeIndex={key}
      remaining={remaining}
      group={groupName} />) : label}
    disableCheckbox={isLoader}
    groupId={groupId}
    isLoader={isLoader}
    disabled={isLoader ? loadingPending : false}
    isAsset={isAsset}
    isDevice={isDevice}
    isLeaf />
);
